import React, { useCallback, useEffect, useState } from 'react';
import { fetchAdminMerchants } from '../services/adminApi';
import { extractErrorMessage } from '../../../shared/utils/errorHandling';

/**
 * Search admin merchant list and pick one (navigate or callback).
 * @param {Object} props
 * @param {(merchantId: string) => void} props.onSelectMerchant
 * @param {string} [props.selectedMerchantId] - highlight current row
 * @param {boolean} [props.autoLoadRecent] - on mount, load 25 most recent merchants
 */
export default function AdminMerchantSearchPicker({
    onSelectMerchant,
    selectedMerchantId,
    autoLoadRecent = false,
}) {
    const [q, setQ] = useState('');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searched, setSearched] = useState(false);

    const runSearch = useCallback(async () => {
        setLoading(true);
        setError(null);
        setSearched(true);
        try {
            const res = await fetchAdminMerchants({
                limit: 25,
                offset: 0,
                q: q.trim() || undefined,
            });
            setItems(res.data?.items || []);
        } catch (e) {
            setItems([]);
            setError(extractErrorMessage(e) || 'Search failed');
        } finally {
            setLoading(false);
        }
    }, [q]);

    useEffect(() => {
        if (!autoLoadRecent) {
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            setSearched(true);
            try {
                const res = await fetchAdminMerchants({ limit: 25, offset: 0 });
                if (!cancelled) {
                    setItems(res.data?.items || []);
                }
            } catch (e) {
                if (!cancelled) {
                    setItems([]);
                    setError(extractErrorMessage(e) || 'Search failed');
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [autoLoadRecent]);

    return (
        <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
                <input
                    type="search"
                    placeholder="Search by business name or email…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            runSearch();
                        }
                    }}
                    className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    autoComplete="off"
                />
                <button
                    type="button"
                    onClick={runSearch}
                    disabled={loading}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50 shrink-0"
                >
                    {loading ? 'Searching…' : 'Search'}
                </button>
            </div>
            <p className="text-xs text-slate-500">
                Leave the field empty and search to list the 25 most recently created merchants.
            </p>
            {error && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
            )}
            {searched && !loading && !error && items.length === 0 && (
                <p className="text-sm text-slate-500">No merchants match this search.</p>
            )}
            {items.length > 0 && (
                <ul
                    className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white text-sm"
                    role="listbox"
                    aria-label="Merchant search results"
                >
                    {items.map((m) => {
                        const isSel = selectedMerchantId && String(m.id) === String(selectedMerchantId);
                        return (
                            <li key={m.id}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={isSel}
                                    onClick={() => onSelectMerchant(m.id)}
                                    className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 flex flex-col gap-0.5 ${
                                        isSel ? 'bg-primary/10 ring-inset ring-1 ring-primary/30' : ''
                                    }`}
                                >
                                    <span className="font-medium text-slate-900">{m.name || '—'}</span>
                                    <span className="text-xs text-slate-600 truncate">{m.email || '—'}</span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
